import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Package, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { verifyAdminToken, logoutAdmin, getAuthHeaders, getAdminUsername } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [settings, setSettings] = useState({
    stripe_api_key: 'sk_test_emergent',
    ghl_webhook_url: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState({
    current_password: '',
    new_username: '',
    new_password: '',
    confirm_password: ''
  });
  const [updatingCredentials, setUpdatingCredentials] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    ingredients: '',
    price: '',
    image_url: '',
    stock: '100',
    category: 'main',
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const isValid = await verifyAdminToken();
    if (!isValid) {
      toast.error('Please login to access admin panel');
      navigate('/admin/login');
    } else {
      fetchMenuItems();
      fetchOrders();
      fetchSettings();
    }
  };

  const handleLogout = () => {
    logoutAdmin();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`, {
        headers: getAuthHeaders()
      });
      setMenuItems(response.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load menu items');
      }
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: getAuthHeaders()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load orders');
      }
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load settings');
      }
    }
  };

  const handleSettingsChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: getAuthHeaders()
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to save settings');
      }
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleTestGHLWebhook = async () => {
    if (!settings.ghl_webhook_url) {
      toast.error('Please enter a GHL webhook URL first');
      return;
    }
    
    setTestingWebhook(true);
    try {
      await axios.post(`${API}/settings/test-ghl-webhook`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Test data sent successfully! Check your GHL webhook to verify.');
    } catch (error) {
      console.error('Error testing webhook:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        const errorMsg = error.response?.data?.detail || 'Failed to send test data';
        toast.error(errorMsg);
      }
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleCredentialsChange = (e) => {
    setAdminCredentials({
      ...adminCredentials,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!adminCredentials.current_password) {
      toast.error('Current password is required');
      return;
    }

    if (!adminCredentials.new_username && !adminCredentials.new_password) {
      toast.error('Please provide new username or new password');
      return;
    }

    if (adminCredentials.new_password && adminCredentials.new_password !== adminCredentials.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (adminCredentials.new_password && adminCredentials.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setUpdatingCredentials(true);
    try {
      const updateData = {
        current_password: adminCredentials.current_password,
        new_username: adminCredentials.new_username || undefined,
        new_password: adminCredentials.new_password || undefined
      };

      const response = await axios.put(`${API}/admin/update-credentials`, updateData, {
        headers: getAuthHeaders()
      });

      // If username changed, update token
      if (response.data.access_token) {
        localStorage.setItem('adminToken', response.data.access_token);
        localStorage.setItem('adminUsername', response.data.username);
      }

      toast.success(response.data.message);
      
      // Reset form
      setAdminCredentials({
        current_password: '',
        new_username: '',
        new_password: '',
        confirm_password: ''
      });

      // Reload page to show new username
      if (response.data.access_token) {
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error) {
      console.error('Error updating credentials:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        const errorMsg = error.response?.data?.detail || 'Failed to update credentials';
        toast.error(errorMsg);
      }
    } finally {
      setUpdatingCredentials(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
      };

      if (editingItem) {
        await axios.put(`${API}/menu-items/${editingItem.id}`, data, {
          headers: getAuthHeaders()
        });
        toast.success('Menu item updated successfully');
      } else {
        await axios.post(`${API}/menu-items`, data, {
          headers: getAuthHeaders()
        });
        toast.success('Menu item created successfully');
      }

      setIsDialogOpen(false);
      setEditingItem(null);
      resetForm();
      fetchMenuItems();
    } catch (error) {
      console.error('Error saving menu item:', error);
      toast.error('Failed to save menu item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      calories: item.calories,
      protein: item.protein,
      carbs: item.carbs,
      fat: item.fat,
      ingredients: item.ingredients,
      price: item.price.toString(),
      image_url: item.image_url,
      stock: item.stock.toString(),
      category: item.category,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await axios.delete(`${API}/menu-items/${id}`, {
          headers: getAuthHeaders()
        });
        toast.success('Menu item deleted successfully');
        fetchMenuItems();
      } catch (error) {
        console.error('Error deleting menu item:', error);
        if (error.response?.status === 401) {
          navigate('/admin/login');
        } else {
          toast.error('Failed to delete menu item');
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      calories: '',
      protein: '',
      carbs: '',
      fat: '',
      ingredients: '',
      price: '',
      image_url: '',
      stock: '100',
      category: 'main',
    });
  };

  const handleUpdateStock = async (id, newStock) => {
    try {
      await axios.put(`${API}/menu-items/${id}`, { stock: parseInt(newStock) }, {
        headers: getAuthHeaders()
      });
      toast.success('Stock updated');
      fetchMenuItems();
    } catch (error) {
      console.error('Error updating stock:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to update stock');
      }
    }
  };

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#f9f7ed' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            style={{ color: '#a80707' }}
            data-testid="back-to-menu-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Menu
          </Button>
          <div className="text-center">
            <h1 className="text-4xl font-bold" style={{ color: '#a80707' }}>Admin Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Logged in as: {getAdminUsername()}</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            style={{ borderColor: '#a80707', color: '#a80707' }}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="menu" className="w-full">
          <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 mb-8" style={{ backgroundColor: 'white' }}>
            <TabsTrigger value="menu" data-testid="menu-tab" style={{ color: '#a80707' }}>Menu Items</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab" style={{ color: '#a80707' }}>Orders</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab" style={{ color: '#a80707' }}>Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="menu">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#a80707' }}>Menu Management</h2>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => {
                        setEditingItem(null);
                        resetForm();
                      }}
                      className="text-white"
                      style={{ backgroundColor: '#a80707' }}
                      data-testid="add-menu-item-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle style={{ color: '#a80707' }}>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            data-testid="item-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="description">Description *</Label>
                          <Input
                            id="description"
                            name="description"
                            required
                            value={formData.description}
                            onChange={handleInputChange}
                            data-testid="item-description-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="price">Price (CA$) *</Label>
                          <Input
                            id="price"
                            name="price"
                            type="number"
                            step="0.01"
                            required
                            value={formData.price}
                            onChange={handleInputChange}
                            data-testid="item-price-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="stock">Stock *</Label>
                          <Input
                            id="stock"
                            name="stock"
                            type="number"
                            required
                            value={formData.stock}
                            onChange={handleInputChange}
                            data-testid="item-stock-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="calories">Calories *</Label>
                          <Input
                            id="calories"
                            name="calories"
                            required
                            value={formData.calories}
                            onChange={handleInputChange}
                            placeholder="e.g., 430 kcal"
                          />
                        </div>
                        <div>
                          <Label htmlFor="protein">Protein *</Label>
                          <Input
                            id="protein"
                            name="protein"
                            required
                            value={formData.protein}
                            onChange={handleInputChange}
                            placeholder="e.g., 40P"
                          />
                        </div>
                        <div>
                          <Label htmlFor="carbs">Carbs *</Label>
                          <Input
                            id="carbs"
                            name="carbs"
                            required
                            value={formData.carbs}
                            onChange={handleInputChange}
                            placeholder="e.g., 43C"
                          />
                        </div>
                        <div>
                          <Label htmlFor="fat">Fat *</Label>
                          <Input
                            id="fat"
                            name="fat"
                            required
                            value={formData.fat}
                            onChange={handleInputChange}
                            placeholder="e.g., 5F"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="ingredients">Ingredients *</Label>
                        <Input
                          id="ingredients"
                          name="ingredients"
                          required
                          value={formData.ingredients}
                          onChange={handleInputChange}
                          placeholder="Separate with /"
                        />
                      </div>
                      <div>
                        <Label htmlFor="image_url">Image URL *</Label>
                        <Input
                          id="image_url"
                          name="image_url"
                          type="url"
                          required
                          value={formData.image_url}
                          onChange={handleInputChange}
                        />
                      </div>
                      <Button type="submit" className="w-full text-white" style={{ backgroundColor: '#a80707' }} data-testid="save-item-btn">
                        {editingItem ? 'Update Item' : 'Add Item'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ backgroundColor: '#f9f7ed' }}>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#a80707' }}>Item</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#a80707' }}>Price</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#a80707' }}>Stock</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#a80707' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {menuItems.map((item) => (
                      <tr key={item.id} data-testid={`admin-item-${item.id}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-16 h-16 object-cover rounded-lg"
                            />
                            <div>
                              <p className="font-semibold text-gray-900">{item.name}</p>
                              <p className="text-sm text-gray-600">{item.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-gray-900">CA${item.price.toFixed(2)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-gray-400" />
                            <Input
                              type="number"
                              value={item.stock}
                              onChange={(e) => handleUpdateStock(item.id, e.target.value)}
                              className="w-20"
                              data-testid={`stock-input-${item.id}`}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(item)}
                              data-testid={`edit-item-btn-${item.id}`}
                            >
                              <Edit className="h-4 w-4" style={{ color: '#a80707' }} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(item.id)}
                              data-testid={`delete-item-btn-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" style={{ color: '#a80707' }} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#a80707' }}>Recent Orders</h2>
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="border-2 rounded-lg p-6" style={{ borderColor: '#a80707' }} data-testid={`order-${order.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-semibold text-gray-900">{order.customer_name}</p>
                        <p className="text-sm text-gray-600">{order.email}</p>
                        <p className="text-sm text-gray-600">{order.phone}</p>
                        <p className="text-sm text-gray-600">{order.address}, {order.city}, {order.postal_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#a80707' }}>CA${order.total.toFixed(2)}</p>
                        <p className="text-sm text-gray-600">{new Date(order.timestamp).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="rounded-lg p-4" style={{ backgroundColor: '#f9f7ed' }}>
                      <p className="text-sm font-semibold mb-2" style={{ color: '#a80707' }}>Order Items:</p>
                      <ul className="space-y-1">
                        {order.items.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-600">
                            {item.name} x {item.quantity} - CA${(item.price * item.quantity).toFixed(2)}
                          </li>
                        ))}
                      </ul>
                      {order.discount_percentage > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-300">
                          <p className="text-sm font-semibold" style={{ color: '#a80707' }}>
                            Discount Applied: {order.discount_percentage}% (-CA${order.discount_amount.toFixed(2)})
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {orders.length === 0 && (
                  <p className="text-center text-gray-600 py-8">No orders yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#a80707' }}>Settings</h2>
              <div className="max-w-2xl space-y-8">

                {/* Admin Credentials Section */}
                <div className="rounded-lg border-2 p-6" style={{ borderColor: '#a80707' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#a80707' }}>Admin Credentials</h3>
                  <form onSubmit={handleUpdateCredentials} className="space-y-4">
                    <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: '#f9f7ed' }}>
                      <p className="text-sm text-gray-700">
                        <strong>Current Admin:</strong> {getAdminUsername()}
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="current_password" className="text-gray-700 font-semibold">Current Password *</Label>
                      <Input
                        id="current_password"
                        name="current_password"
                        type="password"
                        required
                        value={adminCredentials.current_password}
                        onChange={handleCredentialsChange}
                        className="mt-1 border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid="current-password-input"
                        placeholder="Enter your current password"
                      />
                    </div>

                    <div>
                      <Label htmlFor="new_username" className="text-gray-700 font-semibold">New Username (optional)</Label>
                      <Input
                        id="new_username"
                        name="new_username"
                        type="text"
                        value={adminCredentials.new_username}
                        onChange={handleCredentialsChange}
                        className="mt-1 border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid="new-username-input"
                        placeholder="Leave blank to keep current username"
                      />
                    </div>

                    <div>
                      <Label htmlFor="new_password" className="text-gray-700 font-semibold">New Password (optional)</Label>
                      <Input
                        id="new_password"
                        name="new_password"
                        type="password"
                        value={adminCredentials.new_password}
                        onChange={handleCredentialsChange}
                        className="mt-1 border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid="new-password-input"
                        placeholder="Leave blank to keep current password"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum 6 characters
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="confirm_password" className="text-gray-700 font-semibold">Confirm New Password</Label>
                      <Input
                        id="confirm_password"
                        name="confirm_password"
                        type="password"
                        value={adminCredentials.confirm_password}
                        onChange={handleCredentialsChange}
                        className="mt-1 border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid="confirm-password-input"
                        placeholder="Re-enter new password"
                        disabled={!adminCredentials.new_password}
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={updatingCredentials}
                      className="w-full text-white font-semibold"
                      style={{ backgroundColor: '#a80707' }}
                      data-testid="update-credentials-btn"
                    >
                      {updatingCredentials ? 'Updating...' : 'Update Credentials'}
                    </Button>
                  </form>
                </div>
                
                {/* Stripe Settings */}
                <div className="rounded-lg border-2 p-6" style={{ borderColor: '#a80707' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#a80707' }}>Stripe Payment Integration</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="stripe_api_key" className="text-gray-700 font-semibold">Stripe API Key</Label>
                      <Input
                        id="stripe_api_key"
                        name="stripe_api_key"
                        type="password"
                        placeholder="sk_test_... or sk_live_..."
                        value={settings.stripe_api_key}
                        onChange={handleSettingsChange}
                        className="mt-1 border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid="stripe-api-key-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Get your API key from{' '}
                        <a
                          href="https://dashboard.stripe.com/apikeys"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                          style={{ color: '#a80707' }}
                        >
                          Stripe Dashboard
                        </a>
                        {' '}(Use test key for testing, live key for production)
                      </p>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: '#f9f7ed' }}>
                      <p className="text-sm text-gray-700">
                        <strong>Current:</strong> {settings.stripe_api_key?.startsWith('sk_test_') ? '🧪 Test Mode' : settings.stripe_api_key?.startsWith('sk_live_') ? '✅ Live Mode' : 'Not Set'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* GHL Webhook Settings */}
                <div className="rounded-lg border-2 p-6" style={{ borderColor: '#a80707' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#a80707' }}>GoHighLevel (GHL) Integration</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="ghl_webhook_url" className="text-gray-700 font-semibold">GHL Webhook URL</Label>
                      <Input
                        id="ghl_webhook_url"
                        name="ghl_webhook_url"
                        type="url"
                        placeholder="https://services.leadconnectorhq.com/hooks/..."
                        value={settings.ghl_webhook_url || ''}
                        onChange={handleSettingsChange}
                        className="mt-1 border-2"
                        style={{ borderColor: '#a80707' }}
                        data-testid="ghl-webhook-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Order information will be automatically sent to this webhook URL after successful payment
                      </p>
                    </div>
                    
                    {settings.ghl_webhook_url && (
                      <Button
                        onClick={handleTestGHLWebhook}
                        disabled={testingWebhook}
                        variant="outline"
                        className="w-full font-semibold"
                        style={{ borderColor: '#a80707', color: '#a80707' }}
                        data-testid="test-webhook-btn"
                      >
                        {testingWebhook ? 'Sending Test Data...' : '🧪 Test Webhook Connection'}
                      </Button>
                    )}
                    
                    <div className="p-3 rounded-lg" style={{ backgroundColor: '#f9f7ed' }}>
                      <p className="text-sm text-gray-700">
                        <strong>Status:</strong> {settings.ghl_webhook_url ? '✅ Configured' : '⚠️ Not Configured'}
                      </p>
                      {settings.ghl_webhook_url && (
                        <p className="text-xs text-gray-600 mt-1">
                          Orders will be sent to GHL automatically
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveSettings}
                    disabled={settingsLoading}
                    className="text-white font-semibold px-8 py-3"
                    style={{ backgroundColor: '#a80707' }}
                    data-testid="save-settings-btn"
                  >
                    {settingsLoading ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>

                {/* Info Box */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#fff3cd' }}>
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> Changes take effect immediately. Test your integrations after saving to ensure they work correctly.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;